import { inject, Injectable, signal } from '@angular/core';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { GptCompletionMessage } from '@/app/common/general-types/open-ai.dto';
import { MonitorTypeEnum } from '@/app/common/general-types/social-screening.dto';
import { ChatGptService } from '@/app/services/chat-gpt/chat-gpt.service';
import { CreateMonitoring } from '../services/create-monitoring';

/** Delimiter the model emits between its free-text reasoning and the final JSON object. */
const JSON_DELIMITER = '<<<BRIEF_JSON>>>';

/** The shape the model must produce after the delimiter for a monitoring brief. */
export interface MonitoringDraftResult {
  briefTitle?: string;
  briefText?: string;
  contentRequirements?: string;
  contentDonts?: string;
}

/**
 * Builds a monitoring-brief prompt on the frontend and streams reasoning plus JSON
 * through the generic chat completion proxy, then writes the draft into CreateMonitoring.
 */
@Injectable({ providedIn: 'root' })
export class MonitoringBriefAiService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly createMonitoringService = inject(CreateMonitoring);
  private readonly chatGptService = inject(ChatGptService);

  readonly isDrafting = signal(false);
  readonly isCompilingBrief = signal(false);
  readonly streamingThoughts = signal('');
  readonly draftError = signal<string | undefined>(undefined);

  async draftMonitoringBrief(anglePrompt: string): Promise<boolean> {
    if (this.isDrafting()) {
      return false;
    }

    this.isDrafting.set(true);
    this.isCompilingBrief.set(false);
    this.streamingThoughts.set('');
    this.draftError.set(undefined);

    let full = '';
    try {
      for await (const delta of this.chatGptService.streamCompletionMessage(this.buildMessages(anglePrompt))) {
        full += delta;
        this.streamingThoughts.set(this.extractThoughts(full));
        if (full.includes(JSON_DELIMITER)) {
          this.isCompilingBrief.set(true);
        }
      }

      const parsed = this.parseDraftFromResponse(full);
      if (!parsed) {
        this.draftError.set('The AI response could not be read. Please try again.');
        return false;
      }

      return this.applyDraftResult(parsed);
    } catch (err) {
      console.error('MonitoringBriefAiService.draftMonitoringBrief() failed', err);
      this.draftError.set('Something went wrong while generating your monitoring brief. Please try again.');
      return false;
    } finally {
      this.isDrafting.set(false);
      this.isCompilingBrief.set(false);
    }
  }

  /** Applies a parsed GPT draft into CreateMonitoring. Only the four text fields are written. */
  applyDraftResult(parsed: MonitoringDraftResult): boolean {
    const service = this.createMonitoringService;
    const title = parsed.briefTitle?.trim();
    if (title) {
      service.basicInfoModel.set({ briefTitle: title });
    }
    service.briefTextHtml.set(parsed.briefText ?? '');
    service.requirementsHtml.set(parsed.contentRequirements ?? '');
    service.restrictionsHtml.set(parsed.contentDonts ?? '');
    service.provisionMode.set('type');
    return true;
  }

  private buildMessages(anglePrompt: string): GptCompletionMessage[] {
    const brand = this.currentUserService.focussedBrand;
    const product = this.createMonitoringService.selectedProduct();
    const monitorType = this.createMonitoringService.monitorType();
    const watching = monitorType === MonitorTypeEnum.CREATOR_MONITOR_ALL
      ? 'brand-safety issues in newly published creator content'
      : 'newly published content that matches a campaign brief';

    const systemMessage: GptCompletionMessage = {
      role: 'system',
      content: `You are helping a brand define a social monitoring brief. Monitoring watches NEW posts as they go live. It does not screen a creator's historical content and it does not commission new creative.
        First, narrate your reasoning out loud in plain, friendly prose. Do not use JSON during this part.
        When you have finished reasoning, output the exact delimiter line ${JSON_DELIMITER} on its own line, then output ONLY a single valid JSON object and nothing after it.
        Do not wrap the JSON in markdown code fences. Never mention the term "JSON" at any point.`,
    };

    const userMessage: GptCompletionMessage = {
      role: 'user',
      content: `Brand: ${brand?.companyName ?? ''}
      Brand description: ${brand?.about ?? ''}
      Product: ${product?.productName ?? ''}
      Product description: ${product?.productDescription ?? ''}
      This brief is for: ${watching}

      Produce this JSON object after the ${JSON_DELIMITER} delimiter:
      {
        "briefTitle": string,
        "briefText": string,
        "contentRequirements": string,
        "contentDonts": string
      }

      briefTitle is a punchy Title Case name (max 80 characters).
      briefText is HTML overview of what this monitoring watch is for.
      contentRequirements is an HTML list of signals a post should match.
      contentDonts is an HTML list of content that should be flagged.
      Write criteria specific to this brand and product.

      What the brand wants to monitor: ${anglePrompt}`,
    };

    return [systemMessage, userMessage];
  }

  private parseDraftFromResponse(full: string): MonitoringDraftResult | null {
    const jsonText = this.extractJson(full);
    if (!jsonText) {
      return null;
    }
    try {
      return JSON.parse(jsonText) as MonitoringDraftResult;
    } catch (err) {
      console.error('MonitoringBriefAiService.parseDraftFromResponse() JSON parse failed', err);
      return null;
    }
  }

  private extractJson(full: string): string | undefined {
    let candidate = full;
    const delimiterIndex = full.indexOf(JSON_DELIMITER);
    if (delimiterIndex >= 0) {
      candidate = full.slice(delimiterIndex + JSON_DELIMITER.length);
    }
    candidate = candidate.replace(/```json/gi, '').replace(/```/g, '').trim();
    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) {
      return undefined;
    }
    return candidate.slice(start, end + 1);
  }

  private extractThoughts(full: string): string {
    const delimiterIndex = full.indexOf(JSON_DELIMITER);
    return delimiterIndex >= 0 ? full.slice(0, delimiterIndex) : full;
  }
}
